import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useCartStore } from "../stores/cart-store";
import type { Product } from "../lib/types";
import { formatMoney } from "../lib/utils";
import { imageUrl } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ProductLikeButton } from "./product-like-button";

type ProductCardProps = {
  product: Product;
  imageFetchPriority?: "high" | "low" | "auto";
  imageLoading?: "eager" | "lazy";
  imageSizes?: string;
};

export function ProductCard({
  product,
  imageFetchPriority = "auto",
  imageLoading = "lazy",
  imageSizes = "(min-width: 1024px) 31vw, (min-width: 640px) 46vw, calc(100vw - 2rem)"
}: ProductCardProps) {
  const add = useCartStore((state) => state.add);
  const cartQuantity = useCartStore((state) => state.items[product.id]?.quantity ?? 0);
  const [justAdded, setJustAdded] = useState(false);
  const image = product.images[0];
  const outOfStock = product.quantity_remaining <= 0;
  const inCart = cartQuantity > 0;

  function addToCart() {
    add(product);
    setJustAdded(true);
    window.setTimeout(() => setJustAdded(false), 320);
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      className="group overflow-hidden rounded-lg border border-white/[0.08] bg-card/95 shadow-[0_18px_44px_rgba(0,0,0,0.22)] transition duration-300 hover:-translate-y-1 hover:border-accent/35 hover:shadow-[0_24px_60px_rgba(0,0,0,0.34)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <Link to={`/products/${product.slug}`} className="block h-full">
          {image ? (
            <img
              src={imageUrl(image.url)}
              alt={image.alt_text ?? product.name}
              width={800}
              height={600}
              loading={imageLoading}
              fetchPriority={imageFetchPriority}
              decoding="async"
              sizes={imageSizes}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.045]"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No image</div>
          )}
        </Link>
        <ProductLikeButton product={product} className="absolute right-3 top-3" />
      </div>
      <div className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap gap-2">
          <Badge className="bg-muted/45 text-foreground">{product.brand.name}</Badge>
          <Badge>{product.vehicle_brand.name}</Badge>
          <Badge>{product.scale.name}</Badge>
          <Badge className={outOfStock ? "text-destructive" : "text-accent"}>
            {outOfStock ? "Sold out" : product.quantity_remaining <= 2 ? `Hurry! Only ${product.quantity_remaining} left` : `${product.quantity_remaining} left`}
          </Badge>
        </div>
        <Link to={`/products/${product.slug}`} className="block">
          <h3 className="line-clamp-2 min-h-12 text-base font-semibold leading-6 text-foreground transition group-hover:text-accent">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center justify-between gap-3 border-t border-white/[0.08] pt-4">
          <div>
            <p className="text-lg font-semibold tracking-tight">{formatMoney(product.price)}</p>
            {inCart ? <p className="text-xs font-medium text-accent">In cart: {cartQuantity}</p> : null}
          </div>
          <Button size="sm" disabled={outOfStock} className={justAdded ? "cart-added" : ""} onClick={addToCart}>
            {inCart ? "Add more" : "Add"}
          </Button>
        </div>
      </div>
    </motion.article>
  );
}
